/**
 * Unified color system for schedule export wallpapers
 * Used by both server-side (mobile) and client-side (web) export
 *
 * Colors are based on Radix UI system to match the app's design
 */

export interface ExportColors {
	background: string;
	card: string;
	text: string;
	textMuted: string;
	border: string;
	placeholder: string;
	placeholderIcon: string;
	primary: string;
	mustSee: string;
	wantToSee: string;
	sawLive: string;
	sawStream: string;
}

/**
 * Get color scheme for export (always dark — gradient shader renders the background)
 * Ensures consistent colors across web and mobile exports
 */
export function getExportColors(): ExportColors {
	return {
		// Dark mode: Radix Gray Dark palette
		background: "#161618", // grayDark-1
		card: "#1c1c1f", // grayDark-2
		text: "#eeeeef", // grayDark-12 - High contrast for visibility
		textMuted: "#9b9ba7", // grayDark-11
		border: "#2c2c31", // grayDark-6
		placeholder: "#3a3a40", // grayDark-7 - Visible on border bg
		placeholderIcon: "#6e6e80", // grayDark-9 - Visible on placeholder bg
		primary: "#eeeeef", // grayDark-12 (matches text)
		// Status colors: Radix semantic palette
		mustSee: "#6e56cf", // radix-violet-9
		wantToSee: "#ab4aba", // radix-plum-9
		sawLive: "#3e63dd", // radix-indigo-9
		sawStream: "#e54666", // radix-ruby-9
	};
}

/**
 * Calculate contrast ratio between two hex colors
 * Based on WCAG 2.1 formula
 * @returns Contrast ratio (1-21)
 */
export function getContrastRatio(hex1: string, hex2: string): number {
	const getLuminance = (hex: string): number => {
		const rgb = parseInt(hex.slice(1), 16);
		const r = ((rgb >> 16) & 0xff) / 255;
		const g = ((rgb >> 8) & 0xff) / 255;
		const b = (rgb & 0xff) / 255;

		const [rs, gs, bs] = [r, g, b].map((c) =>
			c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4),
		);

		return 0.2126 * rs! + 0.7152 * gs! + 0.0722 * bs!;
	};

	const lum1 = getLuminance(hex1);
	const lum2 = getLuminance(hex2);

	const lighter = Math.max(lum1, lum2);
	const darker = Math.min(lum1, lum2);

	return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Validates that text colors meet WCAG AA contrast requirements (4.5:1)
 * Throws error if contrast is insufficient
 */
export function validateExportColors(colors: ExportColors): void {
	const textContrast = getContrastRatio(colors.text, colors.background);
	const mutedContrast = getContrastRatio(colors.textMuted, colors.background);

	if (textContrast < 4.5) {
		throw new Error(
			`Insufficient text contrast: ${textContrast.toFixed(2)}:1 (minimum 4.5:1)`,
		);
	}

	if (mutedContrast < 3.0) {
		// Slightly lower requirement for muted text (WCAG AA Large Text)
		console.warn(
			`Low muted text contrast: ${mutedContrast.toFixed(2)}:1 (recommended 3.0:1 minimum)`,
		);
	}
}
